import React, { useState, useEffect } from 'react';
import { MenuFilterButton } from '@trainline/depot-web';
import { useIntl } from 'react-intl';
import { css } from 'aphrodite/no-important';

import { TicketTypeFilters, BookingStatusFilters, FilterType, FilterTypes } from '../../types';

import FilterCheckboxes from './FilterCheckboxes';
import messages from './messages';
import styles from './styles';

interface FilterMenuButtonProps {
  type: FilterTypes;
  onApplyClick: (checkedItems: Set<FilterType>) => void;
  applyClearAllClickHandler: (type: FilterTypes) => void;
  appliedFilters: Set<FilterType>;
  label: string;
}

const enumMap = {
  [FilterTypes.TicketType]: Object.values(TicketTypeFilters),
  [FilterTypes.BookingStatus]: Object.values(BookingStatusFilters),
};

const getOptions = (type: keyof typeof enumMap): TicketTypeFilters[] | BookingStatusFilters[] =>
  enumMap[type] as TicketTypeFilters[] | BookingStatusFilters[];

const FilterMenuButton = ({
  type,
  onApplyClick,
  appliedFilters,
  applyClearAllClickHandler,
  label,
}: FilterMenuButtonProps) => {
  const { formatMessage } = useIntl();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Set<FilterType>>(new Set());

  const options = getOptions(type);
  const handleClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleClear = () => {
    setCheckedItems(new Set());
    applyClearAllClickHandler(type);
  };

  const handleDone = () => {
    onApplyClick(checkedItems);
    setCheckedItems(checkedItems);
    setIsMenuOpen(false);
  };

  const handleMenuCheckBoxSelect = (selectedItem: FilterType) => {
    setCheckedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(selectedItem)) {
        updated.delete(selectedItem);
      } else {
        updated.add(selectedItem);
      }
      return updated;
    });
  };

  useEffect(() => {
    if (isMenuOpen) {
      setCheckedItems(new Set(appliedFilters));
    }
  }, [isMenuOpen]);

  const handleAllSelectButtonClick = () => {
    if (checkedItems.size === options.length) {
      setCheckedItems(new Set());
    } else {
      setCheckedItems(new Set([...options]));
    }
  };

  const menuSelectButtonLabel =
    checkedItems.size === getOptions(type).length
      ? formatMessage(messages.deselect)
      : formatMessage(messages.selectAll);

  const menuContent = (
    <FilterCheckboxes
      onSelect={handleMenuCheckBoxSelect}
      checkedItems={[...checkedItems]}
      options={options}
    />
  );

  return (
    <MenuFilterButton
      isMenuOpen={isMenuOpen}
      menuContent={menuContent}
      menuSelectButtonLabel={menuSelectButtonLabel}
      onMenuSelectButtonClick={handleAllSelectButtonClick}
      label={label}
      menuHeading={''}
      buttonClearLabel={formatMessage(messages.clear)}
      menuDoneButtonLabel={formatMessage(messages.done)}
      clearable={false}
      onClear={handleClear}
      onClick={handleClick}
      onDone={handleDone}
      selected={!!appliedFilters.size}
      bubbleProps={{ direction: 'down left', overlayClassName: 'filter-menu' }}
      className={css(styles.filterMenuButton)}
    />
  );
};

export default FilterMenuButton;
